import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, IdentificationBadge, ArrowCounterClockwise, CheckCircle } from "@phosphor-icons/react";
import { FaceCapture } from "@/components/hrms/FaceCapture";
import { blobToImage, faceDescriptor, snapshot } from "@/lib/face";

export const FaceSetup = ({ eid, checklist }) => {
  const [face, setFace] = useState(null);
  const [cam, setCam] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setFace((await api.get(`/hrms/face/${eid}`)).data); } catch (e) { toast.error(formatApiError(e)); }
  }, [eid]);
  useEffect(() => { load(); }, [load]);

  const photoDoc = (checklist || []).find((c) => c.key === "photo" && c.status === "verified" && c.current)?.current;
  const save = async (body) => {
    await api.post(`/hrms/face/${eid}`, body);
    toast.success("Face registered for attendance"); setCam(false); load();
  };
  const fromPhoto = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/hr/documents/${photoDoc.id}/file`, { responseType: "blob" });
      if (!data.type.startsWith("image/")) { toast.error("The verified photo must be an image (JPG/PNG)"); return; }
      const img = await blobToImage(data);
      const descriptor = await faceDescriptor(img);
      if (!descriptor) { toast.error("No clear face found in the verified photo — use live capture"); return; }
      await save({ descriptor, photo: snapshot(img), source: "hr_photo" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset the registered face? The employee must register again before face attendance.")) return;
    try { await api.delete(`/hrms/face/${eid}`); toast.success("Face reset"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const live = async (b) => { try { await save({ ...b, source: "live" }); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="card-flat p-4 flex items-center justify-between flex-wrap gap-3" data-testid="face-setup">
      <div className="flex items-center gap-3">
        {face?.photo && <img src={face.photo} alt="Registered face" className="w-14 h-14 rounded object-cover border border-border" data-testid="face-setup-photo" />}
        <div className="text-[12.5px]" data-testid="face-setup-status">
          {face?.registered_at
            ? <span className="text-green-700 inline-flex items-center gap-1"><CheckCircle size={14} /> Registered {fmtDate(face.registered_at)} · {face.source === "hr_photo" ? "from verified photo" : "live capture"} · by {face.registered_by}</span>
            : <span className="text-amber-700">Face not registered — employee can only use manual attendance until registered.</span>}
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" disabled={!photoDoc || busy} onClick={fromPhoto} title={photoDoc ? "" : "Verify the employee's photo first"} data-testid="face-from-photo">
          <IdentificationBadge size={14} className="mr-1" />{busy ? "Reading photo…" : "Use Verified Photo"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setCam(true)} data-testid="face-live-register"><Camera size={14} className="mr-1" />Live Capture</Button>
        {face?.registered_at && <Button size="sm" variant="ghost" className="text-destructive" onClick={reset} data-testid="face-reset"><ArrowCounterClockwise size={14} className="mr-1" />Reset</Button>}
      </div>
      <FaceCapture open={cam} title="Register employee face" onClose={() => setCam(false)} onCapture={live} />
    </div>
  );
};
